"use client"

import { useContext, useEffect, useMemo, useState } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import InsightsIcon from "@mui/icons-material/Insights"
import { ThemeProvider } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import {
  formatCurrency,
  formatDuracionMinutos,
  formatFecha,
  getEstadoLaboralMeta,
  getEstadoLiquidacionMeta,
  rrhhTheme,
} from "./rrhhUi.js"

const getCurrentPeriod = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
}

const getDateRangeForPeriod = (periodo) => {
  const [year, month] = String(periodo || getCurrentPeriod()).split("-").map(Number)
  const start = new Date(year, (month || 1) - 1, 1)
  const end = new Date(year, month || 1, 0)

  const toInput = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10)

  return {
    fechaDesde: toInput(start),
    fechaHasta: toInput(end),
  }
}

const getTipoConceptoChipStyles = (tipoConcepto) => {
  switch (String(tipoConcepto || "").toUpperCase()) {
    case "HABER":
      return { color: "#166534", backgroundColor: alpha("#22c55e", 0.14) }
    case "DESCUENTO":
      return { color: "#b91c1c", backgroundColor: alpha("#ef4444", 0.14) }
    default:
      return { color: "#475569", backgroundColor: alpha("#64748b", 0.14) }
  }
}

const buildKpiCards = (resumen = {}) => {
  const empleados = resumen.empleados || {}
  const asistencias = resumen.asistencias || {}
  const liquidaciones = resumen.liquidaciones || {}
  const novedades = resumen.novedades || {}

  return [
    {
      id: "empleados-activos",
      titulo: "Empleados activos",
      valor: empleados.activos || 0,
      nota: `Total padrón RRHH: ${empleados.total || 0}`,
      color: "#3b82f6",
    },
    {
      id: "presentismo",
      titulo: "Presentismo operativo",
      valor: `${asistencias.presentismoOperativo || 0}%`,
      nota: `Registros asistencia: ${asistencias.totalRegistros || 0}`,
      color: "#22c55e",
    },
    {
      id: "horas-extra",
      titulo: "Horas extra",
      valor: formatDuracionMinutos(asistencias.minutosExtra),
      nota: `Tardanzas: ${formatDuracionMinutos(asistencias.minutosTarde)}`,
      color: "#f59e0b",
    },
    {
      id: "neto-liquidado",
      titulo: "Neto liquidado",
      valor: formatCurrency(liquidaciones.totalNeto),
      nota: `Liquidaciones período: ${liquidaciones.total || 0}`,
      color: "#10b981",
    },
    {
      id: "novedades-pendientes",
      titulo: "Novedades pendientes",
      valor: novedades.pendientes || 0,
      nota: `Aprobadas: ${novedades.aprobadas || 0}`,
      color: "#64748b",
    },
    {
      id: "revision",
      titulo: "Asistencias a revisar",
      valor: asistencias.requiereRevision || 0,
      nota: `Ausencias: ${asistencias.ausentes || 0}`,
      color: "#ef4444",
    },
  ]
}

const TablaRrhhReportesComponent = () => {
  const { getRrhhReporteResumen } = useContext(ErpContext)

  const [periodo, setPeriodo] = useState(getCurrentPeriod())
  const initialRange = useMemo(() => getDateRangeForPeriod(getCurrentPeriod()), [])
  const [fechaDesde, setFechaDesde] = useState(initialRange.fechaDesde)
  const [fechaHasta, setFechaHasta] = useState(initialRange.fechaHasta)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [reporte, setReporte] = useState(null)

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await getRrhhReporteResumen({
        periodo,
        fechaDesde,
        fechaHasta,
      })

      setReporte(response)
      setError(null)
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError?.message || "No se pudo cargar el reporte RRHH.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const kpiCards = useMemo(() => buildKpiCards(reporte?.resumen), [reporte])

  const asistenciaColumns = [
    {
      field: "empleado",
      headerName: "Empleado",
      minWidth: 220,
      flex: 1.4,
    },
    {
      field: "legajo",
      headerName: "Legajo",
      minWidth: 110,
      flex: 0.7,
    },
    {
      field: "estadoLaboral",
      headerName: "Estado laboral",
      minWidth: 130,
      flex: 0.8,
      renderCell: ({ value }) => {
        const meta = getEstadoLaboralMeta(value)
        return (
          <Chip
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: meta.backgroundColor,
              color: meta.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "asistenciasCount",
      headerName: "Asistencias",
      minWidth: 105,
      flex: 0.65,
    },
    {
      field: "tardes",
      headerName: "Tardes",
      minWidth: 90,
      flex: 0.55,
    },
    {
      field: "ausentes",
      headerName: "Ausentes",
      minWidth: 95,
      flex: 0.6,
    },
    {
      field: "minutosExtra",
      headerName: "Extra",
      minWidth: 120,
      flex: 0.75,
      valueFormatter: (value) => formatDuracionMinutos(value),
    },
    {
      field: "minutosTarde",
      headerName: "Tardanza",
      minWidth: 120,
      flex: 0.75,
      valueFormatter: (value) => formatDuracionMinutos(value),
    },
    {
      field: "minutosAusencia",
      headerName: "Ausencia",
      minWidth: 120,
      flex: 0.75,
      valueFormatter: (value) => formatDuracionMinutos(value),
    },
    {
      field: "liquidacionEstado",
      headerName: "Liquidación",
      minWidth: 130,
      flex: 0.8,
      renderCell: ({ value }) => {
        if (!value) return "-"
        const meta = getEstadoLiquidacionMeta(value)
        return (
          <Chip
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: meta.backgroundColor,
              color: meta.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "liquidacionNeto",
      headerName: "Neto período",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "novedadesPendientes",
      headerName: "Nov. pendientes",
      minWidth: 125,
      flex: 0.8,
    },
  ]

  const liquidacionesColumns = [
    {
      field: "empleado",
      headerName: "Empleado",
      minWidth: 220,
      flex: 1.4,
    },
    {
      field: "legajo",
      headerName: "Legajo",
      minWidth: 110,
      flex: 0.7,
    },
    {
      field: "estado",
      headerName: "Estado",
      minWidth: 130,
      flex: 0.8,
      renderCell: ({ value }) => {
        const meta = getEstadoLiquidacionMeta(value)
        return (
          <Chip
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: meta.backgroundColor,
              color: meta.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "fechaLiquidacion",
      headerName: "Fecha",
      minWidth: 120,
      flex: 0.7,
      valueFormatter: (value) => formatFecha(value),
    },
    {
      field: "totalHaberes",
      headerName: "Haberes",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "totalDescuentos",
      headerName: "Descuentos",
      minWidth: 125,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "totalNeto",
      headerName: "Neto",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "totalMinutosExtra",
      headerName: "Extra",
      minWidth: 120,
      flex: 0.75,
      valueFormatter: (value) => formatDuracionMinutos(value),
    },
  ]

  const novedadesColumns = [
    {
      field: "fechaNovedad",
      headerName: "Fecha",
      minWidth: 120,
      flex: 0.75,
      valueFormatter: (value) => formatFecha(value),
    },
    {
      field: "empleado",
      headerName: "Empleado",
      minWidth: 220,
      flex: 1.4,
    },
    {
      field: "legajo",
      headerName: "Legajo",
      minWidth: 110,
      flex: 0.7,
    },
    {
      field: "categoria",
      headerName: "Categoría",
      minWidth: 120,
      flex: 0.8,
    },
    {
      field: "tipoConcepto",
      headerName: "Impacto",
      minWidth: 120,
      flex: 0.8,
      renderCell: ({ value }) => {
        const styles = getTipoConceptoChipStyles(value)
        return (
          <Chip
            label={value || "-"}
            size="small"
            sx={{
              backgroundColor: styles.backgroundColor,
              color: styles.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      minWidth: 220,
      flex: 1.5,
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "aprobado",
      headerName: "Aprobada",
      minWidth: 110,
      flex: 0.7,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Sí" : "No"}
          size="small"
          sx={{
            backgroundColor: value ? alpha("#22c55e", 0.14) : alpha("#64748b", 0.14),
            color: value ? "#166534" : "#475569",
            fontWeight: 700,
          }}
        />
      ),
    },
  ]

  const topSection = (titulo, rows = [], field) => (
    <Paper sx={{ p: 2, flex: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        {titulo}
      </Typography>
      <Stack spacing={1}>
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Sin datos para el rango seleccionado.
          </Typography>
        )}
        {rows.map((row) => (
          <Paper key={`${titulo}-${row.idEntidad}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {row.empleado}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Legajo {row.legajo}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatDuracionMinutos(row[field])}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Paper>
  )

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Box sx={{ width: "100%", p: { xs: 1, md: 2 } }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="info" icon={<InsightsIcon />}>
            Este tablero consolida únicamente datos del dominio RRHH. La integración con otros módulos del ERP queda pendiente.
          </Alert>

          <Paper sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#1f2937" }}>
                    Reportes RRHH
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Métricas operativas de empleados, asistencia, fichajes, novedades y liquidaciones.
                  </Typography>
                </Box>

                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
                  {loading ? "Actualizando..." : "Actualizar"}
                </Button>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Período"
                  type="month"
                  value={periodo}
                  onChange={(event) => {
                    const nextPeriodo = event.target.value
                    setPeriodo(nextPeriodo)
                    const range = getDateRangeForPeriod(nextPeriodo)
                    setFechaDesde(range.fechaDesde)
                    setFechaHasta(range.fechaHasta)
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { md: 180 } }}
                />

                <TextField
                  label="Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(event) => setFechaDesde(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { md: 180 } }}
                />

                <TextField
                  label="Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(event) => setFechaHasta(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { md: 180 } }}
                />

                <Button variant="contained" onClick={loadData} disabled={loading}>
                  Aplicar corte
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
            {kpiCards.map((card) => (
              <Paper key={card.id} sx={{ p: 2, flex: 1, minWidth: 180, borderLeft: `4px solid ${card.color}` }}>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  {card.titulo}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {card.valor}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {card.nota}
                </Typography>
              </Paper>
            ))}
          </Stack>

          <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
            {topSection("Top tardanzas", reporte?.topTardanzas || [], "minutosTarde")}
            {topSection("Top horas extra", reporte?.topHorasExtra || [], "minutosExtra")}
          </Stack>

          <Paper sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Corte por empleado
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Cruza asistencia, novedades y estado de liquidación del período.
                </Typography>
              </Box>

              <DataGrid
                autoHeight
                rows={reporte?.asistenciaPorEmpleado || []}
                columns={asistenciaColumns}
                loading={loading}
                disableRowSelectionOnClick
                hideFooter
                getRowHeight={() => 56}
                sx={{ minHeight: 420 }}
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Liquidaciones del período
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Resumen operativo de liquidaciones sin integración contable.
                </Typography>
              </Box>

              <DataGrid
                autoHeight
                rows={reporte?.liquidacionesDetalle || []}
                columns={liquidacionesColumns}
                loading={loading}
                disableRowSelectionOnClick
                hideFooter
                getRowHeight={() => 56}
                sx={{ minHeight: 320 }}
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Novedades del período
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Seguimiento de novedades aprobadas y pendientes del mismo período.
                </Typography>
              </Box>

              <DataGrid
                autoHeight
                rows={reporte?.novedadesDetalle || []}
                columns={novedadesColumns}
                loading={loading}
                disableRowSelectionOnClick
                hideFooter
                getRowHeight={() => 56}
                sx={{ minHeight: 320 }}
              />
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </ThemeProvider>
  )
}

export default TablaRrhhReportesComponent
